import React, { useState, useEffect, useCallback } from "react";
import { apiCall, useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { PhoneIncoming, PhoneOutgoing, Download, ChevronLeft, ChevronRight, Play, ExternalLink, Square } from "lucide-react";

export default function CallLogsPage() {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [direction, setDirection] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState("all");

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 50 });
      if (direction !== "all") params.set("direction", direction);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      if (selectedAgent !== "all") params.set("agent_id", selectedAgent);
      const { data } = await apiCall("get", `/call-logs?${params}`);
      setLogs(data.logs);
      setTotal(data.total);
      setPages(data.pages);
    } catch { toast.error("Failed to load call logs"); }
    finally { setLoading(false); }
  }, [page, direction, dateFrom, dateTo, selectedAgent]);

  useEffect(() => {
    if (user?.role === "admin") {
      apiCall("get", "/agents").then(({ data }) => setAgents(data)).catch(() => {});
    }
  }, [user]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const downloadReport = async () => {
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams({ report_type: "calls" });
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reports/download?${params}`, {
        headers: { Authorization: `Bearer ${token}` }, credentials: "include"
      });
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "call_logs_report.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch { toast.error("Download failed"); }
  };

  const getStatusBadge = (status) => {
    const map = {
      initiated: "bg-blue-500/10 text-blue-600 border-blue-500/20",
      ringing: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20",
      answered: "bg-green-500/10 text-green-600 border-green-500/20",
      completed: "bg-gray-500/10 text-gray-600 border-gray-500/20"
    };
    return <Badge className={`text-xs ${map[status] || ""}`}>{status || "Unknown"}</Badge>;
  };

  return (
    <div className="space-y-4" data-testid="call-logs-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {user?.role === "agent" ? "My Call Logs" : "Call Logs"}
          </h2>
          <p className="text-sm text-muted-foreground mt-1">{total} total records</p>
        </div>
        {/* Only admin can download */}
        {user?.role === "admin" && (
          <Button variant="outline" onClick={downloadReport} data-testid="download-calls-btn">
            <Download className="w-4 h-4 mr-2" /> Download Report
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card className="border border-border">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-1">Direction</label>
              <Select value={direction} onValueChange={v => { setDirection(v); setPage(1); }}>
                <SelectTrigger className="w-36" data-testid="direction-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="inbound">Inbound</SelectItem>
                  <SelectItem value="outbound">Outbound</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* Agent filter only for admin */}
            {user?.role === "admin" && (
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-1">Agent</label>
                <Select value={selectedAgent} onValueChange={v => { setSelectedAgent(v); setPage(1); }}>
                  <SelectTrigger className="w-40" data-testid="agent-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Agents</SelectItem>
                    {agents.map(a => <SelectItem key={a._id} value={a._id}>{a.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-1">From</label>
              <Input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }} className="w-40" data-testid="date-from-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-1">To</label>
              <Input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }} className="w-40" data-testid="date-to-input" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border border-border">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs uppercase tracking-wider">Date</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Agent</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Client</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Destination</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Direction</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Duration</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Response</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Recording</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={9} className="text-center py-8"><div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full mx-auto" /></TableCell></TableRow>
              ) : logs.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">No call logs found</TableCell></TableRow>
              ) : (
                logs.map((log, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-sm">{log.created_at ? new Date(log.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : "-"}</TableCell>
                    <TableCell className="text-sm">{log.agent_name || "-"}</TableCell>
                    <TableCell className="text-sm font-medium">{log.client_user_id ? <button className="text-primary hover:underline" onClick={() => {navigator.clipboard.writeText(`${log.client_user_id}`); toast.success("Copied");}}>{log.client_user_id}</button> : "-"}</TableCell>
                    <TableCell className="text-sm font-mono">{log.destination || "-"}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {log.direction === "inbound" ? <PhoneIncoming className="w-3 h-3 mr-1" /> : <PhoneOutgoing className="w-3 h-3 mr-1" />}
                        {log.direction || "N/A"}
                      </Badge>
                    </TableCell>
                    <TableCell>{getStatusBadge(log.status)}</TableCell>
                    <TableCell className="text-sm">{log.duration ? `${log.duration}s` : "-"}</TableCell>
                    <TableCell className="text-sm capitalize">{log.response || "-"}</TableCell>
                    <TableCell>
                      {log.recording_url ? (
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => {
                            const btn = e.currentTarget;
                            const svg = btn.querySelector("svg");
                            let audio = btn._audio;
                            if (audio && !audio.paused) {
                              audio.pause();
                              svg.setAttribute("data-playing", "false");
                              btn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-primary"><polygon points="6 3 20 12 6 21 6 3"></polygon></svg>';
                              return;
                            }
                            if (!audio) { audio = new Audio(log.recording_url); btn._audio = audio; }
                            audio.play();
                            btn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-green-500"><rect x="14" y="4" width="4" height="16"></rect><rect x="6" y="4" width="4" height="16"></rect></svg>';
                            audio.onended = () => { btn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-primary"><polygon points="6 3 20 12 6 21 6 3"></polygon></svg>'; };
                          }} data-testid={`play-recording-${i}`}>
                            <Play className="w-3 h-3 text-primary" />
                          </Button>
                          <a href={log.recording_url} target="_blank" rel="noopener noreferrer" download><Button variant="ghost" size="icon" className="h-7 w-7"><Download className="w-3 h-3" /></Button></a>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">Page {page} of {pages}</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
            <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
      )}
    </div>
  );
}
